// Whole Component Tejashwini
import React, { useState, useEffect, useRef } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import StudentImg from "../images/jhskids.jpg";

function SignInSide() {
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [verifyOTPMsg, setVerifyOTPMsg] = useState("");
  const [resendTimer, setResendTimer] = useState(600);
  const [canResend, setCanResend] = useState(false);
  const [emailError, setEmailError] = useState("");
  const otpInputRefs = useRef([]);

  useEffect(() => {
    if (otpSent && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [otpSent]);

  useEffect(() => {
    let interval;
    if (otpSent && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpSent, resendTimer]);

  const handleGetOtp = async (event) => {
    event.preventDefault();
    setVerifyOTPMsg("");
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Invalid email format");
      return;
    }

    setEmailError("");
    setOtpSent(true);
    setResendTimer(600);
    setCanResend(false);
    setOtp(["", "", "", "", "", ""]);

    try {
      // Send OTP request after switching to OTP screen
      const response = await axios.post(
        `${process.env.REACT_APP_JHS_DEVELOPMENT}/send-otp/signin`,
        { email },
      );
      if (!response.data.userExists) {
        setVerifyOTPMsg("Invalid email. User not found.");
        setOtpSent(false); // Revert to the Sign In screen if user is not found
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setVerifyOTPMsg("Invalid email. User not found.");
      setOtpSent(false); // Revert to the Sign In screen if there's an error
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    const enteredOtp = otp.join("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_JHS_DEVELOPMENT}/send-otp/verify-otp`,
        { email, otp: enteredOtp },
      );

      if (response.data.roleid) {
        // Verify OTP and then navigate based on role
        const role = response.data.roleid;
        const roleName = response.data.roleName;
        const InstituteId = response.data.instituteId;

        if (role) {
          // Store individual values for backward compatibility
          localStorage.setItem("roleID", role);
          localStorage.setItem("user_email", email);
          localStorage.setItem("roleName", roleName);
          localStorage.setItem("instituteId", InstituteId);
          
          // Dispatch custom event to notify OTP context of institute change
          window.dispatchEvent(new Event('instituteChanged'));
          
          // Store complete user object for Dashboard and OTP Control Panel
          const normalizedRoleName = roleName.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
          const userObject = {
            user_id: response.data.userId || null,
            email: email,
            role_id: role,
            role: normalizedRoleName,
            role_name: roleName,
            institute_id: InstituteId,
            roleName: roleName
          };
          localStorage.setItem("user", JSON.stringify(userObject));

          setVerifyOTPMsg("OTP Verified Successfully");
          if (normalizedRoleName === "STUDENT") {
            navigate("/student-management");
            return;
          }

          if (normalizedRoleName === "SUPER_ADMIN" || normalizedRoleName === "SUPER_USER" || normalizedRoleName === "SUPERUSER") {
            navigate("/manage-settings");
            return;
          }

          if (normalizedRoleName === "ADMINISTRATOR" || normalizedRoleName === "DATA_ENTRY_OPERATOR") {
            navigate("/dashboard");
            return;
          }

          if (normalizedRoleName === "ACCOUNTANT") {
            navigate("/fees-management");
            return;
          }

          if (normalizedRoleName === "INVENTORY_MANAGER") {
            navigate("/erp-management");
            return;
          }

          navigate("/dashboard"); // Default to dashboard for unknown roles
          return;
        } else {
          console.log("No role found, navigating to sign-in");
          setVerifyOTPMsg("Invalid role - navigating to sign-in");
          navigate("/signin");
        }
      } else {
        setVerifyOTPMsg("Wrong OTP");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setVerifyOTPMsg("Failed to verify OTP. Please try again.");
      // Clean up if token was set incorrectly due to error
      // localStorage.removeItem('jwt_token');
      localStorage.removeItem("roleID");
      localStorage.removeItem("user_email");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (otpSent) {
      handleVerifyOtp(event);
    } else {
      handleGetOtp(event);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (/^[0-9]$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value !== "" && index < otp.length - 1) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
      if (value === "" && index > 0) {
        document.getElementById(`otp-input-${index - 1}`).focus();
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <ThemeProvider theme={createTheme()}>
      {/* <Layout /> */}
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: `url(${StudentImg})`,
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Grid
          item
          xs={12}
          sm={8}
          md={5}
          component={Paper}
          elevation={6}
          square
          sx={{ height: "100vh" }}
        >
          <Box
            sx={{
              mx: 4,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }} />

            <Typography component="h1" variant="h5">
              {otpSent ? "Verify your email" : "Sign in"}
            </Typography>

            {otpSent && (
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  fontSize: "1.2rem",
                  color: "#555",
                  marginTop: "2%",
                  textAlign: "center",
                  right: "25%",
                }}
              >
                <span style={{ right: "17.8%", top: "31%", fontSize: "15px" }}>
                  {email}
                  <Button
                    sx={{ fontSize: "12px" }}
                    onClick={() => {
                      setOtpSent(false);
                      setEmail("");
                      setVerifyOTPMsg("");
                    }}
                  >
                    Change
                  </Button>
                </span>
              </Typography>
            )}

            {otpSent && (
              <Typography
                variant="body1"
                sx={{ mt: 2, fontSize: "15px", color: "#555", marginTop: "1%" }}
              >
                Enter OTP sent to your email id
              </Typography>
            )}
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1 }}
            >
              {otpSent ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {otp.map((digit, index) => (
                      <TextField
                        key={index}
                        id={`otp-input-${index}`}
                        value={digit}
                        onChange={(e) => handleOtpChange(e, index)}
                        variant="outlined"
                        margin="dense"
                        inputRef={(el) => (otpInputRefs.current[index] = el)}
                        inputProps={{
                          maxLength: 1,
                          "aria-label": `OTP input ${index + 1}`,
                          style: {
                            textAlign: "center",
                            width: "10px",
                            height: "18px",
                            fontSize: "19px",
                            margin: "0 0.5rem",
                            borderRadius: "4px",
                          },
                        }}
                      />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      top: "10px",
                    }}
                  >
                    <Button
                      onClick={handleGetOtp}
                      disabled={!canResend}
                      variant="text"
                    >
                      Resend OTP{" "}
                      {canResend ? "" : `(${formatTime(resendTimer)})`}
                    </Button>
                  </Box>
                </>
              ) : (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={emailError ? emailError : "Email Address"} // Use emailError as label if it exists
                  name="email"
                  autoComplete="email"
                  autoFocus
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError("");
                    setVerifyOTPMsg("");
                  }}
                  error={!!emailError}
                />
              )}
              {verifyOTPMsg && (
                <Typography
                  sx={{
                    top: "20px",
                    color:
                      verifyOTPMsg === "Invalid email. User not found." ||
                      verifyOTPMsg === "Failed to verify OTP. Please try again."
                        ? "red"
                        : verifyOTPMsg === "OTP Verified Successfully"
                          ? "green"
                          : "#0A64A2",
                    fontSize: "18px",
                    fontWeight: "500px",
                    fontFamily: "Times Roman",
                  }}
                >
                  {verifyOTPMsg}
                </Typography>
              )}
              {otpSent ? (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    mb: 2,
                    padding: "10px 0",
                  }}
                >
                  Verify OTP
                </Button>
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    mb: 2,
                    padding: "10px 0",
                  }}
                >
                  Sign in
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

export default SignInSide;